/**
 * Minimal regex-based syntax highlighter for the languages the doc
 * generator commonly emits (ts/tsx/js/jsx, sh/bash, json, sql, css,
 * yaml, toml, md). Renders to a string with `<span>` elements wrapping
 * each token. No runtime deps, < 300 LoC, runs server-side so the
 * client only sees plain HTML.
 *
 * This isn't Shiki-grade — we don't do nested grammar dispatch, only
 * a flat priority list of patterns. That's enough to give code blocks
 * the visual structure people expect in a docs site without the bundle
 * cost. Each pattern wraps matches with `class="tok-X"`; the doc-prose
 * CSS layer assigns the actual colors.
 */
export type Lang =
  | "ts"
  | "tsx"
  | "js"
  | "jsx"
  | "json"
  | "sh"
  | "bash"
  | "sql"
  | "css"
  | "yaml"
  | "toml"
  | "md"
  | "html"
  | "tx";

const ALIASES: Record<string, Lang> = {
  typescript: "ts",
  ts: "ts",
  tsx: "tsx",
  javascript: "js",
  js: "js",
  jsx: "jsx",
  json: "json",
  json5: "json",
  sh: "sh",
  shell: "sh",
  bash: "bash",
  zsh: "sh",
  console: "sh",
  sql: "sql",
  postgres: "sql",
  postgresql: "sql",
  sqlite: "sql",
  mysql: "sql",
  css: "css",
  scss: "css",
  yaml: "yaml",
  yml: "yaml",
  toml: "toml",
  md: "md",
  markdown: "md",
  html: "html",
  xml: "html",
  text: "tx",
  txt: "tx",
  plain: "tx",
};

interface Rule {
  /** Token class — must match a CSS rule under `.doc-prose .tok-*`. */
  tok: string;
  /** Regex with the `g` flag set. */
  re: RegExp;
}

const TS_KEYWORDS =
  "(?:async|await|break|case|catch|class|const|continue|debugger|default|delete|do|else|enum|export|extends|false|finally|for|from|function|if|implements|import|in|instanceof|interface|let|new|null|of|private|protected|public|readonly|return|static|super|switch|this|throw|true|try|type|typeof|undefined|var|void|while|yield)";

const SQL_KEYWORDS =
  "(?:SELECT|FROM|WHERE|GROUP|BY|ORDER|HAVING|JOIN|LEFT|RIGHT|INNER|OUTER|FULL|ON|AS|AND|OR|NOT|IN|EXISTS|LIKE|IS|NULL|TRUE|FALSE|INSERT|INTO|VALUES|UPDATE|SET|DELETE|CREATE|TABLE|INDEX|PRIMARY|KEY|FOREIGN|REFERENCES|UNIQUE|DEFAULT|AUTOINCREMENT|INTEGER|TEXT|REAL|BLOB|VARCHAR|CONSTRAINT|CHECK|CASCADE|WITH|UNION|ALL|DISTINCT|LIMIT|OFFSET|RETURNING|BEGIN|COMMIT|ROLLBACK|TRANSACTION|IF|END|BETWEEN|CASE|WHEN|THEN|ELSE)";

const SH_KEYWORDS =
  "(?:if|then|else|elif|fi|for|while|do|done|in|case|esac|function|return|break|continue|export|local|readonly|source|cd|echo|exit|true|false)";

// We compose rules in priority order: first match wins per position.
// Each rule's regex includes a single non-capturing alternation so the
// `g`-flag `.exec` loop is straightforward.
const RULES: Record<Lang, Rule[]> = {
  ts: [
    { tok: "tok-comment", re: /\/\*[\s\S]*?\*\/|\/\/[^\n]*/g },
    { tok: "tok-string", re: /`(?:\\.|[^`\\])*`|"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'/g },
    { tok: "tok-number", re: /\b\d[\d_.]*(?:n|[eE][+-]?\d+)?\b/g },
    { tok: "tok-keyword", re: new RegExp("\\b" + TS_KEYWORDS + "\\b", "g") },
    { tok: "tok-type", re: /\b[A-Z][A-Za-z0-9_]*\b/g },
    { tok: "tok-fn", re: /\b([A-Za-z_$][\w$]*)(?=\s*\()/g },
  ],
  tsx: [
    { tok: "tok-comment", re: /\/\*[\s\S]*?\*\/|\/\/[^\n]*/g },
    { tok: "tok-string", re: /`(?:\\.|[^`\\])*`|"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'/g },
    { tok: "tok-tag", re: /<\/?[A-Za-z][\w.-]*/g },
    { tok: "tok-number", re: /\b\d[\d_.]*(?:n|[eE][+-]?\d+)?\b/g },
    { tok: "tok-keyword", re: new RegExp("\\b" + TS_KEYWORDS + "\\b", "g") },
    { tok: "tok-type", re: /\b[A-Z][A-Za-z0-9_]*\b/g },
    { tok: "tok-fn", re: /\b([A-Za-z_$][\w$]*)(?=\s*\()/g },
  ],
  js: [
    { tok: "tok-comment", re: /\/\*[\s\S]*?\*\/|\/\/[^\n]*/g },
    { tok: "tok-string", re: /`(?:\\.|[^`\\])*`|"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'/g },
    { tok: "tok-number", re: /\b\d[\d_.]*(?:n|[eE][+-]?\d+)?\b/g },
    { tok: "tok-keyword", re: new RegExp("\\b" + TS_KEYWORDS + "\\b", "g") },
    { tok: "tok-fn", re: /\b([A-Za-z_$][\w$]*)(?=\s*\()/g },
  ],
  jsx: [
    { tok: "tok-comment", re: /\/\*[\s\S]*?\*\/|\/\/[^\n]*/g },
    { tok: "tok-string", re: /`(?:\\.|[^`\\])*`|"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'/g },
    { tok: "tok-tag", re: /<\/?[A-Za-z][\w.-]*/g },
    { tok: "tok-number", re: /\b\d[\d_.]*(?:n|[eE][+-]?\d+)?\b/g },
    { tok: "tok-keyword", re: new RegExp("\\b" + TS_KEYWORDS + "\\b", "g") },
    { tok: "tok-fn", re: /\b([A-Za-z_$][\w$]*)(?=\s*\()/g },
  ],
  json: [
    { tok: "tok-string", re: /"(?:\\.|[^"\\])*"/g },
    { tok: "tok-number", re: /-?\b\d[\d_.]*(?:[eE][+-]?\d+)?\b/g },
    { tok: "tok-keyword", re: /\b(?:true|false|null)\b/g },
  ],
  sh: [
    { tok: "tok-comment", re: /#[^\n]*/g },
    { tok: "tok-string", re: /"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'/g },
    { tok: "tok-var", re: /\$\{[^}\n]+\}|\$[A-Za-z_][\w]*/g },
    { tok: "tok-keyword", re: new RegExp("\\b" + SH_KEYWORDS + "\\b", "g") },
    { tok: "tok-fn", re: /(^|\n|;|&&|\|\|)\s*([a-zA-Z_][\w-]*)/g },
  ],
  bash: [
    { tok: "tok-comment", re: /#[^\n]*/g },
    { tok: "tok-string", re: /"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'/g },
    { tok: "tok-var", re: /\$\{[^}\n]+\}|\$[A-Za-z_][\w]*/g },
    { tok: "tok-keyword", re: new RegExp("\\b" + SH_KEYWORDS + "\\b", "g") },
  ],
  sql: [
    { tok: "tok-comment", re: /--[^\n]*|\/\*[\s\S]*?\*\//g },
    { tok: "tok-string", re: /'(?:''|[^'])*'/g },
    { tok: "tok-number", re: /\b\d+(?:\.\d+)?\b/g },
    { tok: "tok-keyword", re: new RegExp("\\b" + SQL_KEYWORDS + "\\b", "gi") },
  ],
  css: [
    { tok: "tok-comment", re: /\/\*[\s\S]*?\*\//g },
    { tok: "tok-string", re: /"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'/g },
    { tok: "tok-number", re: /-?\b\d+(?:\.\d+)?(?:px|rem|em|%|vw|vh|s|ms|deg)?\b/g },
    { tok: "tok-property", re: /(^|[\n;{])\s*([a-z-]+)(?=\s*:)/g },
    { tok: "tok-selector", re: /(^|\n)\s*([\w.#:>\-+~ *,()[\]"=^$|]+)(?=\s*\{)/g },
  ],
  yaml: [
    { tok: "tok-comment", re: /#[^\n]*/g },
    { tok: "tok-string", re: /"(?:\\.|[^"\\])*"|'(?:''|[^'])*'/g },
    { tok: "tok-property", re: /(^|\n)\s*([\w.-]+)(?=:)/g },
    { tok: "tok-keyword", re: /\b(?:true|false|null|yes|no)\b/g },
  ],
  toml: [
    { tok: "tok-comment", re: /#[^\n]*/g },
    { tok: "tok-string", re: /"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'/g },
    { tok: "tok-property", re: /(^|\n)\s*([\w.-]+)(?=\s*=)/g },
    { tok: "tok-number", re: /\b\d+(?:\.\d+)?\b/g },
    { tok: "tok-keyword", re: /\b(?:true|false)\b/g },
  ],
  md: [
    { tok: "tok-keyword", re: /^#{1,6}\s.*$/gm },
    { tok: "tok-string", re: /`[^`\n]+`/g },
  ],
  html: [
    { tok: "tok-comment", re: /<!--[\s\S]*?-->/g },
    { tok: "tok-string", re: /"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'/g },
    { tok: "tok-tag", re: /<\/?[A-Za-z][\w.-]*|>/g },
  ],
  tx: [],
};

export function normalizeLang(lang: string | null | undefined): Lang | null {
  if (!lang) return null;
  const k = lang.toLowerCase();
  return ALIASES[k] ?? null;
}

/**
 * Tokenize `code` for `lang` and return HTML-escaped output with token
 * spans inserted. Unknown / `text` languages return escaped text only.
 */
export function highlight(code: string, lang: Lang | null): string {
  const rules = lang ? RULES[lang] : null;
  if (!rules || rules.length === 0) return escapeHtml(code);

  const length = code.length;
  // For each position, find the earliest-starting, highest-priority rule.
  type Match = { start: number; end: number; tok: string };
  const matches: Match[] = [];

  for (const rule of rules) {
    rule.re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = rule.re.exec(code)) !== null) {
      const groupIdx = m.length > 2 ? 2 : m.length > 1 && m[1] !== undefined ? 1 : 0;
      const matched = groupIdx === 0 ? m[0] : m[groupIdx];
      if (matched == null) continue;
      let start = m.index;
      if (groupIdx > 0) {
        // Find the offset of the captured group within the full match.
        // The first capture group is what we want to span.
        const before = m[0] ?? "";
        const off = before.indexOf(matched);
        if (off >= 0) start = m.index + off;
      }
      const end = start + matched.length;
      if (end <= start) {
        if (m[0]?.length === 0) rule.re.lastIndex++;
        continue;
      }
      matches.push({ start, end, tok: rule.tok });
    }
  }

  // Sort by start ASC, then by length DESC so longer matches win at ties.
  matches.sort((a, b) => a.start - b.start || (b.end - b.start) - (a.end - a.start));

  // Drop overlapping later matches (earlier-priority rule wins because
  // rules are applied in order and we sorted stable by start).
  const cleaned: Match[] = [];
  let cursor = 0;
  for (const m of matches) {
    if (m.start < cursor) continue;
    cleaned.push(m);
    cursor = m.end;
  }

  let out = "";
  let pos = 0;
  for (const m of cleaned) {
    if (m.start > pos) out += escapeHtml(code.slice(pos, m.start));
    out += `<span class="${m.tok}">${escapeHtml(code.slice(m.start, m.end))}</span>`;
    pos = m.end;
  }
  if (pos < length) out += escapeHtml(code.slice(pos));
  return out;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}
